/**
 * @ipc/validation · src/schemas/env.ts — валидация конфигурации (раздел 13,
 * раздел 15 "Деплой и конфигурация" — детали инфры там, здесь HTTP/API-слой
 * общесистемных env-параметров). Разбирается один раз на старте процесса
 * (`loadEnv()` в apps/backend/src/index.ts). Ключи Multicard хранятся
 * per-tenant в БД/секрет-хранилище тенанта — НЕ здесь.
 */
import { z } from 'zod/v4';

/** Docker compose injects "" for unset ${VAR}; treat empty as absent for optionals. */
function emptyToUndefined(value: unknown): unknown {
  if (value === '' || value === undefined || value === null) {
    return undefined;
  }
  return value;
}

export const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  PORT: z.coerce.number().int().positive().default(3000),
  DATABASE_URL: z.string().url(),
  REDIS_URL: z.string().url(),
  JWT_SECRET: z.string().min(32),
  API_KEY_PEPPER: z.string().min(16),
  // HMAC-ключ для ApiKey.keyHash (X-Api-Key). Обязателен: без fail-fast process.env
  // мог остаться пустым, а guard в production бросал бы только на первом запросе.
  API_KEY_HMAC_SECRET: z.string().min(16),
  // Telegram Login Widget: токен бота (@BotFather) — секретный ключ проверки подписи входа.
  // optional, чтобы dev/test-старт не требовал бота; без него вход через виджет отдаёт 401.
  // empty string from compose → undefined (иначе regex валит старт).
  BOT_TOKEN: z.preprocess(
    emptyToUndefined,
    z
      .string()
      .regex(/^\d+:[A-Za-z0-9_-]+$/, 'Некорректный формат BOT_TOKEN (ожидается <id>:<token>)')
      .optional(),
  ),
  // Публичный username бота (без @) — нужен фронту для рендера Login Widget.
  TELEGRAM_BOT_USERNAME: z.preprocess(emptyToUndefined, z.string().min(1).optional()),
  TRUST_PROXY: z.enum(['true', 'false']).default('false'),
  MULTICARD_SANDBOX: z.enum(['true', 'false']).default('true'),
  MULTICARD_WEBHOOK_IPS: z.string().default(''),
});
export type Env = z.infer<typeof envSchema>;

/**
 * Разбирает `process.env` строгой схемой. Бросает с человекочитаемым
 * перечнем недостающих/невалидных ключей — процесс не должен подняться
 * с `undefined` в рантайме (раздел 13, сценарий "env-валидация валит старт").
 */
export function loadEnv(source: NodeJS.ProcessEnv = process.env): Env {
  const parsed = envSchema.safeParse(source);
  if (!parsed.success) {
    const keys = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
    throw new Error(`Некорректная конфигурация окружения: ${keys}`);
  }
  return parsed.data;
}
